import {
  ExamEventStatus,
  RegistrationStatus,
  RegistrationType,
  useGetAllProvincesQuery,
} from '@exam-center/graphql';
import { parseAsArrayOf, parseAsStringLiteral, parseAsTimestamp, useQueryStates } from 'nuqs';

interface UseExamEventScheduleSearchParamsOption {
  defaultValues?: {
    startedAt?: Date;
    endedAt?: Date;
  };
}

export default function useExamEventScheduleSearchParams(
  options?: Readonly<UseExamEventScheduleSearchParamsOption>,
) {
  const { defaultValues } = options ?? {};

  const { data } = useGetAllProvincesQuery();

  const provinceNames =
    data?.ecGetAllProvinces?.provinces?.map((province) => province.nameTh) ?? [];

  const [
    { status, registrationStatus, registrationType, startedAt, endedAt, province },
    setExamEventScheduleSearchParams,
  ] = useQueryStates({
    registrationStatus: parseAsArrayOf(
      parseAsStringLiteral(Object.values(RegistrationStatus)),
    ).withDefault([]),
    registrationType: parseAsArrayOf(
      parseAsStringLiteral(Object.values(RegistrationType)),
    ).withDefault([]),
    status: parseAsStringLiteral([...Object.values(ExamEventStatus), 'ALL']).withDefault('ALL'),
    province: parseAsArrayOf(parseAsStringLiteral(Object.values(provinceNames))).withDefault([]),
    startedAt: parseAsTimestamp.withDefault(
      defaultValues?.startedAt ?? new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    ),
    endedAt: parseAsTimestamp.withDefault(
      defaultValues?.endedAt ??
        (() => {
          const date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
          date.setHours(23, 59, 59, 999);
          return date;
        })(),
    ),
  });

  const changeStatus = (status: ExamEventStatus | 'ALL' | null) => {
    setExamEventScheduleSearchParams({ status });
  };

  const changeRegistrationStatus = (status: RegistrationStatus[] | null) => {
    setExamEventScheduleSearchParams({ registrationStatus: status });
  };

  const changeRegistrationType = (registrationType: RegistrationType[] | null) => {
    setExamEventScheduleSearchParams({ registrationType });
  };

  const changePeriod = (startedAt: Date | null, endedAt: Date | null) => {
    setExamEventScheduleSearchParams({ startedAt, endedAt });
  };

  const changeProvince = (province: string[] | null) => {
    setExamEventScheduleSearchParams({ province });
  };

  return [
    { status, registrationStatus, registrationType, startedAt, endedAt, province },
    {
      changePeriod,
      changeRegistrationStatus,
      changeRegistrationType,
      changeStatus,
      changeProvince,
    },
  ] as const;
}
